import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { ChartsOrQuotePageQueryParams, SpecificChartFunctionality } from '../../constants/common'
import { useModelState } from '../../model-hooks/use-model-state'
import { useModelRef } from '../../model-hooks/useModelRef'
import Chart from '../../models/chart'
import Quote from '../../models/quote'
import { QuoteFetchType } from '../../models/quote/constants'
import { createRequestAbortController, removeFromAbortCache } from '../../utils/abort_controller'
import { decodeQueryString, encodeQueryString } from '../../utils/query_string'
import { getLeftOffset } from '../renderUtils'

const getQuoteRequestKey = (chartModel: Chart) =>
  `quoteReq-${chartModel.ticker}-${chartModel.instrument}-${chartModel.id}`

export function useChartControls({
  chartModel: unwatchedChartModel,
  isInteractive,
}: {
  chartModel: Chart | null
  isInteractive: boolean
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const chartModel = useModelState(unwatchedChartModel, {
    watchProperties: ['ticker', 'timeframe', 'dateRange'],
  })
  const chartLayoutModel = useModelState(chartModel?.chart_layout() ?? null, {
    watchProperties: ['idea'],
  })
  const chartModelRef = useModelRef(chartModel)
  const [isLoading, setIsLoading] = React.useState(false)
  const abortControllerKeyRef = React.useRef('')
  const isIdeaChart = !!chartLayoutModel?.idea?.id

  const updateChartModelWithNewQuote = React.useCallback((quote: Quote) => {
    const chart = chartModelRef.current
    if (!chart) {
      return
    }

    const chartLayoutModel = chart.chart_layout()
    const isSameTicker = chart.getQuoteRawTicker() === quote.getRawTicker()

    chart.getAllPanes().forEach((pane) => {
      if (!!pane.getChartOrIndicatorElement()) {
        pane.updateAttribute('scaleRange', null)
      }
    })

    chart.updateAttributes({ leftOffset: getLeftOffset({ quote, chartModel: chart }), quote, isScrolled: true })

    // Refetch all other chart quotes as the main one is already refetched so we can filter it out but other quotes (from perf for example) should be refetched as well
    chart
      .getAllQuotes()
      .filter((chartQuote) => !chartQuote.eql(quote))
      .forEach((quote) => {
        quote.fetchData({ fetchType: QuoteFetchType.NewerData })
      })

    for (const element of chart.getAllElements()) {
      if (element.isDrawing()) {
        // We want to remove all drawings on ticker change but if autosave is enabled we let the autosave hook take care of that
        if (isSameTicker && chartLayoutModel.isPreserveDrawingsActive) {
          element.instance.updateScales()
        } else {
          element.destroyCascade()
        }
      }
    }

    // The only dependency here is a ref , so no need to add it to dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchQuote = React.useCallback(async () => {
    if (!isInteractive || !chartModel || isIdeaChart) {
      return
    }
    setIsLoading(true)

    // Change all idea quotes to non idea quotes and refetch because we don't need them anymore but they may cause inconsistencies if they are in cache
    const ideaQuotes = chartModel.getAllQuotes().filter((chartQuote) => chartQuote.ideaID)
    ideaQuotes.forEach((quote) => {
      quote.updateAttributes({ ideaID: undefined })
      quote.fetchData({ fetchType: QuoteFetchType.Refetch })
    })

    const { ticker, instrument, timeframe } = chartModel
    const { premarket, aftermarket } = Quote.first()
    const patterns = chartModel.getHasPatterns()
    const currentQuote = chartModel.quote()
    if (currentQuote.getRawTicker() !== ticker || currentQuote.timeframe !== timeframe || ideaQuotes.length > 0) {
      abortControllerKeyRef.current = getQuoteRequestKey(chartModel)
      const abortController = createRequestAbortController({
        key: abortControllerKeyRef.current,
        shouldAbortRunningRequest: !Quote.select<Quote>(
          (quote) =>
            quote.ticker === ticker &&
            quote.timeframe === timeframe &&
            quote.isFetching &&
            chartModel.chart_layout().isIdeaId(quote.ideaID)
        ),
      })

      const quote = await Quote.get({
        ticker,
        instrument,
        timeframe,
        options: {
          premarket,
          aftermarket,
          patterns,
          cachePredicate: (quote) => quote.ideaID === undefined,
          fetchNewDataOnCachedQuote: true,
        },
        abortController,
      })

      if (quote.wasFetchAborted) {
        // Check if the quote is also used in other chars, as the quote may be reused if we already have a quote with the same options
        const isQuoteUsedInOtherCharts = chartModel
          .chart_layout()
          .getAllCharts()
          .filter((chart) => !chartModel.eql(chart))
          .some((chart) => quote.eql(chart.quote()))

        // If the quote hasn't been fetched yet and is not anywhere else we can destroy the model
        if (quote.fetchedAt === -1 && !isQuoteUsedInOtherCharts) {
          quote.destroy()
        }

        return
      }

      removeFromAbortCache(abortControllerKeyRef.current)

      updateChartModelWithNewQuote(quote)
    }

    setIsLoading(false)

    // We don't want to create new function on any chartModel change as it may be the same chart with different object instance
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chartModel?.id,
    chartModel?.ticker,
    chartModel?.timeframe,
    chartModel?.instrument,
    isIdeaChart,
    updateChartModelWithNewQuote,
    isInteractive,
  ])

  React.useEffect(() => {
    fetchQuote()
  }, [fetchQuote])

  // There are several ways how to turn off dateRange, e.g. moving the charts
  // This effect listens to dateRange changes on chartmodel and updates the url if there's a change
  // Consider refactoring this part so we only update chart models on route changes not the other way around
  React.useEffect(() => {
    if (chartModel) {
      const decodedQueryString = decodeQueryString<ChartsOrQuotePageQueryParams>(location.search)
      const rangesArray = chartModel
        .chart_layout()
        .getAllCharts()
        .map((chart) => chart.dateRange)
      const ranges = rangesArray.every((i) => !i) ? undefined : rangesArray.join(',')

      if (decodedQueryString.r !== ranges) {
        navigate({
          search: `?${encodeQueryString({
            ...decodedQueryString,
            r: ranges,
          })}`,
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartModel?.dateRange])

  // https://github.com/finvizhq/charts/pull/1253/files#r1052266805
  // React.useEffect(
  //   () => () => {
  //     if (isInteractive && abortControllerKeyRef.current !== '') {
  //       abortRequest(abortControllerKeyRef.current)
  //     }
  //   },
  //   [isInteractive]
  // )

  React.useEffect(() => {
    const chart = chartModelRef.current
    const chartLayout = chart?.chart_layout()
    const quote = chart?.quote()

    let shouldRecalculateOnCharts = false
    if (chartLayout?.specificChartFunctionality === SpecificChartFunctionality.chartPage && !isIdeaChart) {
      const settings = chartLayoutModel?.settings
      const barWidth = 3
      const barMargin = 1
      if (settings) {
        const hasSameBarWidth = [
          settings.ChartSettings.center.barWidth,
          settings.IndicatorSettings.center.barWidth,
        ].every((value) => value === barWidth)
        const hasSameBarMargin = [
          settings.ChartSettings.center.barMargin,
          settings.IndicatorSettings.center.barMargin,
        ].every((value) => value === barMargin)

        shouldRecalculateOnCharts = !hasSameBarWidth || !hasSameBarMargin
        if (shouldRecalculateOnCharts) {
          settings.ChartSettings.center.barWidth = barWidth
          settings.IndicatorSettings.center.barWidth = barWidth
          settings.ChartSettings.center.barMargin = barMargin
          settings.IndicatorSettings.center.barMargin = barMargin

          chartLayoutModel.updateAttributes({ settings })
        }
      }
    }

    if (
      chart &&
      quote &&
      (chartLayout?.specificChartFunctionality === SpecificChartFunctionality.quotePage || shouldRecalculateOnCharts) &&
      !isIdeaChart
    ) {
      const leftOffset = getLeftOffset({ quote, chartModel: chart })
      chart.updateAttributes({ leftOffset })
    }
    // This effect should run only on timeframe change & idea change
    // The reason we need to recalculate the left offset is that quote timeframe change changes chart and chart bars sizes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartModel?.quote()?.timeframe, isIdeaChart])

  return { isLoading }
}
